import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useResetPasswordMutation } from "../app/api/console/consoleAuthApiSlice";
import LogoWordmark from "../assets/zenyt_logo_wordmark.svg";

const MIN_PASSWORD_LENGTH = 12;

// The emailed link carries the token in the URL fragment (#token=...), which
// browsers never send to a server. Read it once, then drop it from the address
// bar so it does not stay in history.
const readTokenFromHash = () => {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  return params.get("token") || "";
};

const inputClass =
  "w-full px-3.5 py-2.5 text-body-sm text-text-primary bg-surface-neutral rounded-xl border border-border-subtle focus:outline-none focus:ring-2 focus:ring-accent-purple/30 focus:border-accent-purple/50 placeholder:text-text-tertiary transition-all";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [token] = useState(readTokenFromHash);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("The two passwords don't match.");
      return;
    }
    try {
      const data = await resetPassword({ token, newPassword: password }).unwrap();
      navigate("/login", { replace: true, state: { notice: data.message } });
    } catch (err) {
      setError(err.data?.detail || "Something went wrong. Try again in a few minutes.");
    }
  };

  return (
    <div className='flex min-h-screen items-center justify-center bg-gradient-to-br from-sidebar-gradient-from to-sidebar-gradient-to font-sans'>
      <div className='w-full max-w-sm'>
        <div className='bg-white rounded-2xl shadow-xl p-8'>
          <div className='flex flex-col items-center mb-8'>
            <img src={LogoWordmark} alt='Zenyt' className='h-7 mb-3' />
            <span className='text-text-tertiary text-body-sm'>Choose a new password</span>
          </div>

          {!token ? (
            <div className='px-3 py-2 text-body-xs text-severity-high bg-severity-high/5 rounded-lg text-center'>
              This reset link is incomplete. Open the link from your email again, or ask for a new one.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className='space-y-4'>
              <div>
                <label htmlFor='new-password' className='block text-body-xs font-medium text-text-secondary mb-1.5'>
                  New password
                </label>
                <input
                  id='new-password'
                  name='new-password'
                  type='password'
                  autoComplete='new-password'
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  className={inputClass}
                  placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor='confirm-password' className='block text-body-xs font-medium text-text-secondary mb-1.5'>
                  Confirm new password
                </label>
                <input
                  id='confirm-password'
                  name='confirm-password'
                  type='password'
                  autoComplete='new-password'
                  required
                  className={inputClass}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </div>

              {error && (
                <div className='px-3 py-2 text-body-xs text-severity-high bg-severity-high/5 rounded-lg text-center'>
                  {error}
                </div>
              )}

              <button
                type='submit'
                disabled={isLoading}
                className='w-full py-2.5 text-body-sm font-semibold text-white bg-button-primary rounded-xl hover:bg-button-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-2'>
                {isLoading ? "Saving..." : "Set new password"}
              </button>
            </form>
          )}

          <div className='mt-6 flex justify-center gap-4'>
            <Link to='/forgot-password' className='text-body-xs text-accent-purple hover:underline'>
              Get a new link
            </Link>
            <Link to='/login' className='text-body-xs text-accent-purple hover:underline'>
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
